<template>
  <div class="diagnosis-case-grid">
    <vt-side-panel :visible="toggleCaseSidepanel">
      <case-sidepanel
        ref="caseSidePanel"
        @closeCaseSidepanel="closeCaseSidepanel"
      />
    </vt-side-panel>
    <vt-side-panel :visible="toggleSampleSidepanel">
      <sample-sidepanel
        ref="sampleSidePanel"
        @closeSampleSidepanel="closeSampleSidepanel"
      />
    </vt-side-panel>
    <b-row class="search-block mb-3 mt-1">
      <b-col
        xl="4"
        lg="6"
        md="6"
        sm="6"
      >
        <searcher-module
          name="diagnosisSearcher"
          popover-id="popover-diagnosis-grid"
          :error-message="$t('diagnosis.errors.validIdentifier')"
          :is-current-disabled="true"
          case-search
          subportion-search
          @subportionSearch="onSubportionSearch($event)"
          @studySearch="onStudySearch($event)"
        />
      </b-col>
      <b-col
        xl="8"
        lg="6"
        md="6"
        sm="6"
        class="text-right"
      >
        <div style="margin-top: 5px;">
          <b-button
            v-shortkey="['alt', 'b']"
            qa="diagnosis-case-grid-batch-actions-button"
            class="btn__link px-3"
            @shortkey.prevent="$refs.batchActionsModal.show()"
            @click="$refs.batchActionsModal.show()"
          >
            <i
              class="far fa-clone mr-2"
              aria-hidden="true"
            />{{ $t('diagnosis.batchActionsModal.title') }}
          </b-button>
          <b-button
            v-shortkey="['f10']"
            qa="diagnosis-case-grid-batch-patients-button"
            class="btn__link px-3"
            @shortkey.prevent="openPatientModal"
            @click="openPatientModal"
          >
            <i
              class="far fa-users mr-2"
              aria-hidden="true"
            />{{ $tc('global.patients') }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!--Diagnosis Grid-->
    <div class="grid-container">
      <GridHeader
        :qa="componentId"
        :gridtitle="selectedOption && $t(selectedOption.label)"
        :gridsubtitle="msgSelectedRows"
      >
        <grid-display-options
          v-if="gridApi"
          :ui-component="gridUiComponent"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            id="samplesCleanFilters"
            v-tooltip="$t('global.cleanFilter')"
            v-shortkey="['ctrl', 'alt', 'f']"
            qa="diagnosis-case-grid-batch-clean-button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            type="button"
            @shortkey.prevent="cleanFilters"
            @click="cleanFilters"
          >
            <i
              class="far fa-filter"
              aria-hidden="true"
            />
          </button>
        </div>

        <vt-report-export-buttons
          source="diagnosis-case-grid"
          :export-report="exportReport"
        />
      </GridHeader>
      <vt-grid
        qa="diagnosis-case-grid-elements-div"
        class="diagnosis-case-grid"
        :short-key="shortKey"
        :master-detail="masterDetail"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :get-row-height="getRowHeight"
        :detail-row-height="detailRowHeight"
        :component-code="selectedOptionUiComponent"
        :component-id="componentId"
        :use-detail-custom-config="masterDetail"
        :detail-cell-renderer-params="detailCellRendererParams"
      />
    </div>
    <common-incident-modal-tree ref="incidentModalTree" />
    <add-custom-filter-modal
      id="addFilterModal"
      ref="addFilterModal"
      @ok="onAddFilterOk"
    />
    <print-component ref="printComponent" />
    <search-patient-modal
      ref="searchPatientModal"
      id-modal="searchPatientModal"
    />
    <incident-form-modal
      id="incidentFormModal"
      ref="incidentFormModal"
      core-entity="CASE"
      @incident-added="reloadRowData"
    />
    <common-attachment-tree-modal
      v-if="patient"
      ref="attachmentTreeModal"
      :patient="patient"
      :laboratory-id="labId"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :request-created="true"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <modal-wrapper ref="reassignPathologistModalWrapper">
      <reassign-pathologist-modal @reloadGrid="reloadRowData" />
    </modal-wrapper>
    <modal-add-amendment
      ref="modalAddAmendment"
      @amendmentAdded="reloadRowData"
    />
    <modal-wrapper ref="batchActionsModal">
      <batch-actions-modal />
    </modal-wrapper>
    <case-batch-validator
      ref="caseValidation"
      @reloadGrid="reloadRowData"
    />
  </div>
</template>

<script>
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import { diagnosisCaseGridMasterColumnDefs } from '@/modules/diagnosisModule/views/grid/config/diagnosis-case-grid-master.column-defs'
import { diagnosisCaseGridDetailColumnDefs } from '@/modules/diagnosisModule/views/grid/config/diagnosis-case-grid-detail.column-defs'
import { DiagnosisCaseDatasource } from '@/modules/diagnosisModule/views/grid/config/diagnosis-case-datasource'
import diagnosisCaseService from '@/modules/diagnosisModule/services/DiagnosisCaseService'
import diagnosisSampleService from '../../services/DiagnosisSampleService'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import SearcherModule from '@/components/SearcherModule'
import diagnosisSideBarStore from '@/modules/diagnosisModule/sideBar/DiagnosisSideBarStore'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import { isEmpty, isNil, omitBy, uniqBy } from 'lodash'
import Encoder from '@/utils/encoder'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import SearchPatientModal from '@/components/searcherPatientModal/SearchPatientModal'
import CaseSidepanel from '@/modules/diagnosisModule/views/grid/CaseSidepanel'
import SampleSidepanel from '@/modules/diagnosisModule/views/grid/SampleSidepanel'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import incidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import AddFileModal from '@/components/AddFileModal'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import diagnosisMixin from '@/modules/diagnosisModule/mixins/diagnosisMixin'
import genderResource from '@/api/resources/GenderResource'
import ModalAddAmendment from '@/modules/diagnosisModule/views/grid/ModalAddAmendment'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import BatchActionsModal from '@/modules/diagnosisModule/components/BatchActionsModal/BatchActionsModal'
import ReassignPathologistModal from '@/modules/diagnosisModule/modals/ReassignPathologistModal'
import CaseBatchValidator from '@/modules/diagnosisModule/views/grid/CaseBatchValidator'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { GridUtils } from '@/shared/util/grid-utils'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { aiAnalysisState } from '@/api/resources/entityState/ai-analysis-state'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'DiagnosisCaseGrid',
    components: {
        ReassignPathologistModal,
        AddFileModal,
        CommonAttachmentTreeModal,
        incidentFormModal,
        GridHeader,
        VtGrid,
        SearcherModule,
        AddCustomFilterModal,
        SearchPatientModal,
        CaseSidepanel,
        SampleSidepanel,
        CommonIncidentModalTree,
        ModalAddAmendment,
        GridDisplayOptions,
        BatchActionsModal,
        CaseBatchValidator
    },
    mixins: [
        diagnosisMixin,
        CaseOperationGuardMixin,
        IncidentOperationGuardMixin,
        GridExportMixin
    ],
    props: {
        initMode: {
            type: Object,
            default: () => ({ value: 'compact', icon: 'fal fa-align-justify' })
        },
        initFilterModel: { type: Object, default: null },
        initSortModel: { type: Array, default: () => [] },
        initPaginator: { type: Object, default: null },
        initTabId: { type: Number, default: 0 }
    },
    data() {
        return {
            mode: this.initMode,
            gridUiComponent: 'uicomponent.diagnosis.casegrid',
            delayDoubleClick: 200,
            preventClick: false,
            detailRowHeight: 300,
            patient: null,
            toggleCaseSidepanel: false,
            toggleSampleSidepanel: false,
            selectedDiagnosisCase: null,
            columnDefs: [],
            gridApi: null,
            columnApi: null,
            sideBarState: diagnosisSideBarStore.state,
            encoder: new Encoder(),
            detailCellRendererParams: {
                detailGridOptions: {
                    floatingFilter: false,
                    suppressContextMenu: true,
                    onRowClicked: this.onSampleRowClicked,
                    pagination: false,
                    // TODO: Israel --> el sidebar de columnas se corta si el grid no es muy alto (con dos registros)
                    sideBar: gridSideBarConfig,
                    rowModelType: AgGridRowModelType.CLIENT_SIDE,
                    columnDefs: diagnosisCaseGridDetailColumnDefs()
                },
                getDetailRowData: async (params) => {
                    const samples = await this.getMasterDetailRowData(params)
                    params.successCallback(samples)
                }
            },
            selectedRows: [],
            entitiesIdArray: [],
            shortKey: { keys: ['ctrl', 'enter'], callFn: this.openCaseInform }
        }
    },
    computed: {
        selectedOptionUiComponent() {
            return this.selectedOption?.uiComponent
        },
        componentId() {
            return this.selectedOption?.id?.toString()
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.CASE
        },
        isSelectedOptionValidated() {
            return this.selectedOption && (this.selectedOption.id === 'validatedCasesToday' ||
                this.selectedOption.id === 'validatedCasesThisWeek' ||
                this.selectedOption.id === 'validatedCasesThisMonth')
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        },
        userInformation() {
            return this.$store.getters.userInformation
        },
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getContextMenuItems: this.getContextMenuItems,
                sideBar: gridSideBarConfig,
                onRowDoubleClicked: (row) => this.onRowDoubleClicked(row.data),
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true // remove number in Group Column
                    }
                }
            }
        },
        masterDetail() {
            return true
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        sideBarFilterModel() {
            return this.sideBarState.filterModel
        },
        labId() {
            return this.$store.getters.labId
        },
        sideBarExternalFilterModel() {
            return this.sideBarState.externalFilterModel
        },
        sideBarCustomFilterModel() {
            return this.sideBarState.customFilterModel
        },
        sideBar() {
            // changes when the sideBar is expanded or contracted
            return this.sideBarState.sideBar
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return 25
            }
            return 40
        },
        displayedColumns() {
            return this.columnApi.getAllDisplayedColumns()
        },
        sortModel() {
            return this.gridApi.getSortModel()
        },
        selectedOption() {
            return this.sideBarState.selectedOption
        },
        isSelectedCasesWithConsults() {
            return this.selectedOption?.id.startsWith('CONSULTATION_') ?? false
        },
        responsibilitySelected() {
            return this.sideBarState.config.responsibility?.code
        }
    },
    watch: {
        sideBarExternalFilterModel(newValue) {
            if (!isEmpty(newValue)) {
                this.reloadRowData(true)
            }
        },
        sideBar() {
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit()
            }
        }
    },
    created() {
        this.setColumnDefs()
    },
    mounted() {
        if (this.$route.params.filterModel) {
            this.initFilterModel = this.$route.params.filterModel
        }
        this.$eventHub.$on('filterModelDiagnose', this.applySidebarFilterCheck)
    },
    beforeDestroy() {
        this.$eventHub.$off('filterModelDiagnose', this.applySidebarFilterCheck)
    },
    methods: {
        async setColumnDefs() {
            await aiAnalysisState.loadStates()
            await genderResource.initialize()
            this.columnDefs = diagnosisCaseGridMasterColumnDefs(this.onIdCellClicked, this.onExpandClicked)
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        showReassingPathologistModal(caseDataBatch, laboratoryId) {
            this.$refs.reassignPathologistModalWrapper.show([caseDataBatch, laboratoryId])
        },
        async getMasterDetailRowData(params) {
            const [err, response] = await this.toCall(diagnosisSampleService.getAllSamplesByLaboratoryAndCaseId(
                params.data.laboratory.labId,
                params.data.casId
            ))
            if (err) {
                this.showErrorModalGlobal()
                return []
            }
            return response
        },
        async onSideBarClickUpdateFilter(option) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const filters = {
                filterModel: this.gridApi.getFilterModel()
            }
            const [err] = await this.toCall(diagnosisSideBarStore.updateCustomOption(option, filters))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            await diagnosisSideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterUpdated'))
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        openCaseInform() {
            if (this.selectedRows.length === 1) {
                this.onRowDoubleClicked(this.selectedRows[0])
            }
        },
        showIncidentModal(params) {
            const patientId = params.patient.id
            const defaultOption = {
                coreEntity: 'CASE',
                casId: params.casId
            }
            this.$refs.incidentModalTree.show(defaultOption, patientId)
        },
        showIncidentFormModal(params) {
            const entityInformation = {
                coreEntity: 'CASE',
                casId: params.casId
            }
            this.$refs.incidentFormModal.show(null, entityInformation, null, params.laboratory.labId)
        },
        showAttachmentModal(params) {
            this.patient = {
                patId: params.patient.id,
                centerIdentifier: params.patient.centerIdentifier
            }
            this.$nextTick(() => {
                this.$refs.attachmentTreeModal.showCase(params.casId)
            })
        },
        showAddFileModal(params) {
            this.entitiesIdArray = [params.laboratory.labId, params.casId]
            this.$refs.addFileModalWrapper.show()
        },
        onAttachmentsAdded() {
            this.reloadRowData()
        },
        async validateCases(casesData) {
            this.$refs.caseValidation.show(casesData)
        },
        async transcribeCase(caseData) {
            if (this.$_canTranscribeToCase(caseData.status.code, caseData.transcriptionRequired)) {
                this.updateLoading(true)
                const [err] = await this.toCall(diagnosisCaseService.transcribeCase(caseData.laboratory.labId, caseData.casId))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                this.showToastSuccessGlobal(this.$t('diagnosis.transcribeSuccess'))
                this.reloadRowData(true)
            }
        },
        async informCase(caseData) {
            this.updateLoading(true)
            const [err] = await this.toCall(diagnosisCaseService.informCase(caseData.laboratory.labId, caseData.casId))
            this.updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err)
                return
            }
            this.$_showToastSuccess(this.$t('diagnosis.informSuccess'))
            this.reloadRowData(true)
        },
        getContextMenuItems(params) {
            if (params?.node) {
                const caseData = params.node.data
                const caseDataBatch = this.selectedRows.length ? params.api.getSelectedNodes().map(node => node.data) : [caseData]
                const caseDataBatchByLaboratory = uniqBy(caseDataBatch, 'laboratory.labId')
                return [
                    {
                        name: this.$t('diagnosis.btns.report'),
                        action: () => {
                            const getCaseReportPDFParams = {
                                laboratoryId: caseData.laboratory.labId,
                                caseId: caseData?.casId,
                                caseLabel: caseData?.casCasenumber,
                                print: false
                            }
                            HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
                        },
                        cssClasses: this.selectedRows.length <= 1 && this.$_canPrintCaseReport() ? '' : ['not-show']
                    },
                    {
                        name: this.$t('diagnosis.btns.validate'),
                        action: () => this.validateCases(caseDataBatch),
                        cssClasses: this.$_disabledValidateCases(caseDataBatch) ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('diagnosis.btns.inform'),
                        action: () => this.informCase(caseData),
                        cssClasses: this.$_canBeCaseInformed(caseData) && this.$_canInformCases() ? '' : ['not-show']
                    },
                    {
                        name: this.$t('diagnosis.labels.transcribe'),
                        action: () => this.transcribeCase(caseData),
                        cssClasses: this.$_canTranscribeToCase(caseData.status.code, caseData.transcriptionRequired) ? '' : ['not-show']
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.viewIncidents'),
                        action: () => this.showIncidentModal(caseData),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.registerIncident'),
                        action: () => this.showIncidentFormModal(caseData),
                        cssClasses: this.selectedRows.length <= 1 && this.$_canCreateIncident() && this.$_canBeIncidentCreatedToCase(caseData) ? '' : ['not-show']
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.viewAttach'),
                        action: () => this.showAttachmentModal(caseData),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.addAttach'),
                        action: () => this.showAddFileModal(caseData),
                        cssClasses: this.selectedRows.length <= 1 && this.$_canAttachDocumentToCase && this.$_canBeDocumentAttachedToNotValidatedCase(caseData) ? '' : ['not-show']
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.reassignPathologist'),
                        action: () => this.showReassingPathologistModal(caseDataBatch, caseDataBatchByLaboratory[0].laboratory.labId),
                        cssClasses: !this.$_isCaseValidated(caseData.status.code) && !this.$_isCaseInAmendment(caseData.status.code) ? '' : ['not-show']
                    },
                    {
                        name: this.$t('global.showInGlobalLocator'),
                        action: () => this.showInGlobalLocator(caseData.casCasenumber),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                    }
                ]
            }
            return []
        },
        closeCaseSidepanel(value) {
            this.toggleCaseSidepanel = value
        },
        closeSampleSidepanel(value) {
            this.toggleSampleSidepanel = value
        },
        applySidebarFilterCheck(filterModel) {
            if (this.gridApi) {
                this.gridApi.setFilterModel(filterModel)
            } else {
                this.initFilterModel = filterModel
            }
        },
        onGridReady(api) {
            this.gridApi = api.api
            this.columnApi = api.columnApi
            this.addLastAmendmentDateColumn()
            if (this.initFilterModel) {
                this.gridApi.setFilterModel(this.initFilterModel)
                this.initFilterModel = null
            } else if (this.sideBarFilterModel) {
                this.applySideBarFilterModel()
            }
            this.gridApi?.deselectAll()
            this.gridApi.setServerSideDatasource(
                new DiagnosisCaseDatasource(
                    this.labId,
                    this.sideBarState.config.viewAllEnabled,
                    this.responsibilitySelected,
                    this.selectedOption,
                    this.sideBarExternalFilterModel))
        },
        getRowHeight(params) {
            if (params?.node && params?.node.detail) {
                return this.detailRowHeight
            } else {
                return this.rowHeight
            }
        },
        onSampleRowClicked({ data: diagnosisSample }) {
            const isSame =
                this.selectedDiagnosisSample &&
                diagnosisSample.id === this.selectedDiagnosisSample.id

            if (isSame) {
                this.toggleSampleSidepanel = !this.toggleSampleSidepanel
                this.toggleCaseSidepanel = false
                return
            }
            this.selectedDiagnosisSample = diagnosisSample
            this.$refs.sampleSidePanel.loadSidePanelData(diagnosisSample, diagnosisSample.caseStudyLaboratoryId)
            this.toggleCaseSidepanel = false
            this.toggleSampleSidepanel = true
        },
        onIdCellClicked({ data: diagnosisCase }) {
            setTimeout(() => {
                if (!this.preventClick) {
                    const isSame =
                        this.selectedDiagnosisCase &&
                        diagnosisCase.casId === this.selectedDiagnosisCase.casId

                    if (isSame) {
                        this.toggleCaseSidepanel = !this.toggleCaseSidepanel
                        this.toggleSampleSidepanel = false
                        return
                    }
                    this.selectedDiagnosisCase = diagnosisCase
                    this.$refs.caseSidePanel.loadAssaysData(this.selectedDiagnosisCase.casId, this.selectedDiagnosisCase.casCasenumber, this.selectedDiagnosisCase.laboratory.labId)
                    this.toggleCaseSidepanel = true
                    this.toggleSampleSidepanel = false
                }
            }, this.delayDoubleClick)
        },
        reloadRowData(emitSummary = false) {
            this.gridApi?.deselectAll()
            this.gridApi?.setServerSideDatasource(
                new DiagnosisCaseDatasource(
                    this.labId,
                    this.sideBarState.config.viewAllEnabled,
                    this.responsibilitySelected,
                    this.selectedOption,
                    this.sideBarExternalFilterModel))
            this.addLastAmendmentDateColumn()
            if (emitSummary) {
                this.$emit('updateSummary')
            }
        },
        onViewAllToggled() {
            this.reloadRowData()
        },
        onViewResponsibilityChanged() {
            this.reloadRowData()
        },
        applySideBarFilterModel() {
            if (this.gridApi) {
                this.gridApi.setFilterModel(this.sideBarFilterModel)
                this.addLastAmendmentDateColumn()
            }
        },
        onAfterMasterRowExpanded(api, detailNode, parentDetail) {
            detailNode.data.expandedHeight = this.calculateDetailHeight(detailNode)
            detailNode.setRowHeight(detailNode.data.expandedHeight)
            api.onRowHeightChanged()
            if (parentDetail) {
                this.onAfterMasterRowExpanded(
                    parentDetail.gridApi,
                    parentDetail,
                    parentDetail.data.parentDetail
                )
            }
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            GridUtils.changeRowHeightGrid(this.gridApi, selectedOption.size)
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        cleanFilters() {
            this.applySideBarFilterModel()
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                const caseIds = this.selectedRows.map(row => row.casId)

                const externalFilterModel = omitBy(this.sideBarState.externalFilterModel, isNil)
                let filterModel =
                    !isEmpty(externalFilterModel)
                        ? Object.assign(this.gridApi.getFilterModel(), externalFilterModel)
                        : this.gridApi.getFilterModel()

                const customFilterModel = omitBy(this.selectedOption.customFilterModel, isNil)
                filterModel =
                    !isEmpty(customFilterModel)
                        ? Object.assign(filterModel, customFilterModel)
                        : filterModel
                if (this.isSelectedCasesWithConsults) {
                    return diagnosisCaseService.exportToConsultationCases(
                        this.labId,
                        this.selectedOption.task.exportUrl,
                        this.$tc(this.selectedOption.label),
                        this.encoder.parseBooleans(this.gridApi.getFilterModel()),
                        GridUtils.getSortValue(this.sortModel),
                        columns,
                        caseIds,
                        exportType)
                } else {
                    if (this.sideBarState.config.viewAllEnabled) {
                        return diagnosisCaseService.exportToAllCases(
                            this.labId,
                            this.selectedOption.caseSituation,
                            this.$tc(this.selectedOption.label),
                            this.encoder.parseBooleans(filterModel),
                            GridUtils.getSortValue(this.sortModel),
                            columns,
                            caseIds,
                            exportType)
                    } else {
                        return diagnosisCaseService.exportToMyOwnCases(
                            this.labId,
                            this.responsibilitySelected,
                            this.selectedOption.caseSituation,
                            this.$tc(this.selectedOption.label),
                            this.encoder.parseBooleans(filterModel),
                            GridUtils.getSortValue(this.sortModel),
                            columns,
                            caseIds,
                            exportType)
                    }
                }
            }
            return null
        },
        onSideBarClickAddFilter() {
            this.$refs.addFilterModal.show()
        },
        addLastAmendmentDateColumn() {
            const lastAmendmentField = 'lastAmendmentDate'
            const columnAmendmentIndex = this.columnDefs.findIndex((column) => column.field === lastAmendmentField)
            if (this.isSelectedOptionValidated && columnAmendmentIndex < 0) {
                this.columnDefs.push({
                    type: 'amendmentRenderer',
                    headerName: this.$t('diagnosis.grid.caseGrid.amendments'),
                    headerTooltip: this.$t('diagnosis.grid.caseGrid.amendments'),
                    field: lastAmendmentField,
                    exportField: lastAmendmentField,
                    exportType: 'dateTime',
                    sortable: false,
                    width: 110,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate('far fa-money-check-edit')
                    }
                })
            } else if (!this.isSelectedOptionValidated && columnAmendmentIndex >= 0) {
                this.columnDefs.splice(columnAmendmentIndex, 1)
            }
        },
        async onAddFilterOk(filter) {
            const params = {
                label: filter.label,
                filterModel: this.gridApi.getFilterModel(),
                customFilterModel: this.selectedOption.customFilterModel,
                caseSituation: this.selectedOption.caseSituation
            }
            await customFilterResource.create(
                this.sideBarState.componentCode,
                params
            )
            await diagnosisSideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterAdded'))
        },
        onRowDoubleClicked(caseData) {
            this.goToDiagnosisCaseView(caseData.laboratory.labId, caseData.casId)
        },
        openPatientModal() {
            this.$refs.searchPatientModal.show()
        }
    }
}
</script>

<style lang="scss">

// Master detail diagnosis
.diagnosis-case-grid .ag-details-row .ag-details-grid{
  background: #f3f7ff;
}

.diagnosis-case-grid .ag-details-row .ag-details-grid .ag-root-wrapper-body.ag-layout-normal{
    padding: 0.5rem;
}

.diagnosis-case-grid .vt-side-panel .status.pending {
    color: rgb(255, 207, 77);
}

.diagnosis-case-grid .vt-side-panel .status.in-process {
    color: rgb(70, 128, 255);
}

.diagnosis-case-grid .vt-side-panel .status.canceled {
    color: rgb(252, 98, 116);
}

.diagnosis-case-grid .vt-side-panel .status.finished {
    color: rgb(46, 204, 113);
}

</style>

<style lang="scss" scoped>
.diagnosis-case-grid {
  height: 100%;
}

.diagnosis-case-page {
  display: flex;
}

.sr-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem;
  flex-shrink: 0;
}

.sr-header-buttons {
  margin-right: 1.5px;
}
</style>
